// @flow

import type { AuthBloc } from "../../blocs/authBloc";
import type Project, {
  UpdateProjectRequest
} from "../../models/Project/Project";
import { apiAxios } from "../../config/AxiosConfig";
import { CreateProjectRequest } from "../../models/Project/Project";
import { AppToaster } from "../../components/Toaster/Toaster";
import { Intent, Spinner } from "@blueprintjs/core";
import history from "../../history";
class ProjectApi {
  authBloc: AuthBloc;

  constructor(authBloc: AuthBloc) {
    this.authBloc = authBloc;
  }

  async getAll(projectStates:boolean): Promise<Array<Project>> {
    const tenantId = this.authBloc.getCurrentTenant();

    const response = await apiAxios.get(
      `/tenants/${tenantId}/projects?projectStates=${projectStates ? projectStates:[]}`,
      this.buildHeaders()
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(response.status);
    }
  }

  async createProject(
    createProjectRequest: CreateProjectRequest
  ): Promise<Project> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.post(
        `/tenants/${tenantId}/projects`,
        createProjectRequest,
        {...this.buildHeaders(), skipErrorToast: true}
      );
      if (response.status === 201) {
        history.goBack();
        setTimeout(() => {
          AppToaster.show({
            message: "Project Created",
            intent: Intent.PRIMARY
          });
        }, 300);

        return response.data;
      } else {
        throw new Error(response.status);
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to create project. Please try again.',
        intent: Intent.DANGER
      });
    }
  }

  async updateCustomMilestone(
    updateCustomMilestone: any,
    projectId: string,
  ): Promise<any> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.post(
        `/tenants/${tenantId}/projects/${projectId}/milestones`,
        updateCustomMilestone,
        {...this.buildHeaders(), skipErrorToast: true}
      );
      if (response.status === 200 || response.status === 204) {
        return response.data;
      } else {
        throw new Error(response.status);
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to update milestone. Please try again.',
        intent: Intent.DANGER
      });
    }
  }

  async updateCustomMilestoneDate(
    request: any,
    projectId: string,
  ): Promise<any> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.patch(
        `/tenants/${tenantId}/projects/${projectId}`,
        request,
        {...this.buildHeaders(), skipErrorToast: true}
      );
      if (response.status === 200 || response.status === 204) {
        return response.data;
      } else {
        throw new Error(response.status);
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to update milestone date. Please try again.',
        intent: Intent.DANGER
      });
    }
  }

  async updateProject(
    projectId: string,
    updateProjectRequest: UpdateProjectRequest
  ): Promise<void> {
    const tenantId = this.authBloc.getCurrentTenant();
    const response = await apiAxios
      .patch(
        `/tenants/${tenantId}/projects/${projectId}`,
        updateProjectRequest,
        {...this.buildHeaders(), skipErrorToast: true}
      )
      .then(response => {
        if (response.status === 204) {
          if (updateProjectRequest.fromEditProject) {
            history.goBack();
            setTimeout(() => {
              AppToaster.show({
                message: "Project Updated",
                intent: Intent.PRIMARY
              });
            }, 300);
          }
          if(updateProjectRequest.projectState){
            AppToaster.show({
              message: `Project ${updateProjectRequest.projectState}`,
              intent: Intent.PRIMARY
            });
          }
          return response.data;
        } else {
          throw new Error(response.status);
          // return false
        }
      })
      .catch(error => {
        AppToaster.show({
          message: error.response?.data?.message || 'Failed to update project. Please try again.',
          intent: Intent.DANGER
        });
        return false;
      });
    return response;
  }

  async addVendorToProject(
    projectId: string,
    vendorIdToAdd: string
  ): Promise<Project> {
    const tenantId = this.authBloc.getCurrentTenant();

    const response = await apiAxios.post(
      `/tenants/${tenantId}/projects/${projectId}/vendors`,
      { id: vendorIdToAdd },
      this.buildHeaders()
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(response.status);
    }
  }

  async removeVendorFromProject(
    projectId: string,
    vendorToRemove: string
  ): Promise<Project> {
    const tenantId = this.authBloc.getCurrentTenant();

    const response = await apiAxios.delete(
      `/tenants/${tenantId}/projects/${projectId}/vendors/${vendorToRemove}`,
      this.buildHeaders()
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(response.status);
    }
  }

  async removeProject(projectId: string): Promise<Project> {
    const tenantId = this.authBloc.getCurrentTenant();
    const response = await apiAxios
      .delete(`/tenants/${tenantId}/projects/${projectId}`, {...this.buildHeaders(), skipErrorToast: true})
      .then(response => {
        if (response.status === 200) {
          AppToaster.show({
            message: "Project Deleted",
            intent: Intent.PRIMARY
          });
          return response.data;
        } else {
          throw new Error(response.status);
        }
      })
      .catch(err => {
        AppToaster.show({
          message: err.response?.data?.message || 'Failed to delete project. Please try again.',
          intent: Intent.DANGER
        });
        throw err;
      });
    return response;
  }

  buildHeaders = () => {
    const accessToken = this.authBloc.getCurrentToken();
    const auth = "Bearer " + accessToken;
    return { headers: { Authorization: auth } };
  };
}

export default ProjectApi;
